import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../services/api';
import './EmailAuth.css';

function EmailAuth({ onAuthenticated, noCampaign }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState('email'); // 'email', 'code', or 'totvs'
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [localNoCampaign, setLocalNoCampaign] = useState(false);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateEmail(email)) {
      setError(t('email.error'));
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.authenticate(email);
      
      if (response.authMethod === 'email') {
        setStep('code');
      } else if (response.token) {
        onAuthenticated(response);
      }
    } catch (err) {
      if (err.response?.data?.authMethod === 'totvs') {
        if (email.endsWith('@totvs.com.br')) {
          const extractedUsername = email.replace('@totvs.com.br', '');
          setUsername(extractedUsername);
        }
        setStep('totvs');
        setError('');
      } else if (err.response?.data?.code === 'NO_PUBLIC_CAMPAIGN') {
        setLocalNoCampaign(true);
      } else {
        setError(err.response?.data?.error || t('errors.generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!code || code.length !== 4) {
      setError(t('code.error'));
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.validateCode(email, code);
      onAuthenticated(response);
    } catch (err) {
      setError(err.response?.data?.error || t('code.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError('');
    try {
      await authAPI.authenticate(email);
    } catch (err) {
      setError(err.response?.data?.error || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleTotvsSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username || !password) {
      setError(t('totvs.error'));
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.authenticateTotvs(email, username, password);
      onAuthenticated(response);
    } catch (err) {
      setError(err.response?.data?.error || t('totvs.error'));
    } finally {
      setLoading(false);
    }
  };

  if (noCampaign || localNoCampaign) {
    return (
      <div className="email-auth">
        <div className="auth-container no-campaign-container">
          <div className="no-campaign-icon">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M8 11V7a4 4 0 0 1 8 0v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <circle cx="12" cy="16" r="1.5" fill="currentColor"/>
            </svg>
          </div>
          <h2 className="no-campaign-title">{t('noCampaignScreen.title')}</h2>
          <p className="no-campaign-message">{t('noCampaignScreen.message')}</p>
          <p className="no-campaign-check-back">{t('noCampaignScreen.checkBack')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="email-auth">
      <div className="auth-container">
        {step === 'email' ? (
          <form onSubmit={handleEmailSubmit} className="auth-form">
            <h1 className="auth-title">{t('email.title')}</h1>
            <input
              type="email"
              className="auth-input"
              placeholder={t('email.placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
            <p className="auth-disclaimer">{t('email.disclaimer')}</p>
            {error && <p className="auth-error">{error}</p>}
            <button type="submit" className="auth-button" disabled={loading}>
              {loading ? t('loading') : t('email.submit')}
            </button>
          </form>
        ) : step === 'code' ? (
          <form onSubmit={handleCodeSubmit} className="auth-form">
            <h1 className="auth-title">{t('code.title')}</h1>
            <p className="code-description">
              {t('code.description')} <strong>{email}</strong>
            </p>
            <input
              type="text"
              className="auth-input code-input"
              placeholder={t('code.placeholder')}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
              disabled={loading}
              maxLength={4}
              autoFocus
            />
            {error && <p className="auth-error">{error}</p>}
            <button type="submit" className="auth-button" disabled={loading}>
              {loading ? t('loading') : t('code.submit')}
            </button>
            <button
              type="button"
              className="resend-button"
              onClick={handleResendCode}
              disabled={loading}
            >
              {t('code.resend')}
            </button>
          </form>
        ) : (
          <form onSubmit={handleTotvsSubmit} className="auth-form">
            <h1 className="auth-title">{t('totvs.title')}</h1>
            <input
              type="text"
              className="auth-input"
              placeholder={t('totvs.usernamePlaceholder')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              autoFocus
            />
            <input
              type="password"
              className="auth-input"
              placeholder={t('totvs.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
            {error && <p className="auth-error">{error}</p>}
            <button type="submit" className="auth-button" disabled={loading}>
              {loading ? t('loading') : t('totvs.submit')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default EmailAuth;
